import React from 'react';
import {
    DashboardMenuItem,
    MenuItemLink,
    MenuProps,
    useSidebarState,
    useTranslate,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import DatasetIcon from '@mui/icons-material/Dataset';
import TuneIcon from '@mui/icons-material/Tune';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Submenu from './Submenu';
import user from '@core/users';
import role from '@core/roles';
import goods from '@src/core/goods';
import store from '@src/core/store';
import price from '@src/core/price';
import target from '@src/core/target';
import schedule from '@src/core/schedule';
import order from '@src/core/order';
import orderDetail from '@src/core/order-detail';
import setting from '@src/core/setting';
import { entityResource as ent } from '@src/entityResource';

type MenuName = 'masterMenu' | 'transactionMenu' | 'settingMenu';

export const Menu = ({ dense = false }: MenuProps) => {
    const translate = useTranslate();
    const location = useLocation();
    const [open] = useSidebarState();
    const masterPath = React.useMemo(() => ['/goods', '/price', '/store'], []);
    const transactionPath = React.useMemo(
        () => ['/schedule', '/target', '/order', '/order-detail'],
        []
    );
    const settingPath = React.useMemo(
        () => ['/roles', '/users', '/settings'],
        []
    );
    const [menuState, setMenuState] = React.useState<Record<MenuName, boolean>>(
        {
            masterMenu: false,
            transactionMenu: false,
            settingMenu: false,
        }
    );

    React.useEffect(() => {
        setMenuState({
            masterMenu: masterPath.some(path =>
                location.pathname.startsWith(path)
            ),
            transactionMenu: transactionPath.some(path =>
                location.pathname.startsWith(path)
            ),
            settingMenu: settingPath.some(path =>
                location.pathname.startsWith(path)
            ),
        });
    }, [location, masterPath, settingPath, transactionPath]);

    const handleToggle = (menuName: MenuName) => {
        setMenuState({
            ...menuState,
            [menuName]: !menuState[menuName],
        });
    };

    return (
        <Box
            sx={{
                width: open ? 280 : 72,
                marginTop: 1,
                marginBottom: 1,
                transition: theme =>
                    theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
            }}
        >
            <DashboardMenuItem />
            <Submenu
                handleToggle={() => handleToggle('masterMenu')}
                isOpen={menuState.masterMenu}
                name="app.menu.master.name"
                icon={<DatasetIcon />}
                dense={dense}
            >
                <MenuItemLink
                    to={ent.GOODS.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        `app.menu.master.submenu.goods.name`,
                        { smart_count: 2 }
                    )}
                    leftIcon={<goods.icon />}
                    dense={dense}
                />
                <MenuItemLink
                    to={ent.STORE.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        `app.menu.master.submenu.store.name`,
                        { smart_count: 2 }
                    )}
                    leftIcon={<store.icon />}
                    dense={dense}
                />
                <MenuItemLink
                    to={ent.PRICE.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        `app.menu.master.submenu.price.name`,
                        { smart_count: 2 }
                    )}
                    leftIcon={<price.icon />}
                    dense={dense}
                />
            </Submenu>
            <Submenu
                handleToggle={() => handleToggle('transactionMenu')}
                isOpen={menuState.transactionMenu}
                name="app.menu.transaction.name"
                icon={<ShoppingCartIcon />}
                dense={dense}
            >
                <MenuItemLink
                    to={ent.TARGET.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        `app.menu.transaction.submenu.target.name`,
                        { smart_count: 2 }
                    )}
                    leftIcon={<target.icon />}
                    dense={dense}
                />
                <MenuItemLink
                    to={ent.SCHEDULE.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        `app.menu.transaction.submenu.schedule.name`,
                        { smart_count: 2 }
                    )}
                    leftIcon={<schedule.icon />}
                    dense={dense}
                />
                <MenuItemLink
                    to={ent.ORDER.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        `app.menu.transaction.submenu.order.name`,
                        { smart_count: 2 }
                    )}
                    leftIcon={<order.icon />}
                    dense={dense}
                />
                <MenuItemLink
                    to={ent.ORDER_DETAIL.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        `app.menu.transaction.submenu.orderDetail.name`,
                        { smart_count: 2 }
                    )}
                    leftIcon={<orderDetail.icon />}
                    dense={dense}
                />
            </Submenu>
            <Submenu
                handleToggle={() => handleToggle('settingMenu')}
                isOpen={menuState.settingMenu}
                name="app.menu.setting.name"
                icon={<TuneIcon />}
                dense={dense}
            >
                <MenuItemLink
                    to={ent.SETTING.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        'app.menu.setting.submenu.setting.name',
                        { smart_count: 2 }
                    )}
                    leftIcon={<setting.icon />}
                    dense={dense}
                />
                <MenuItemLink
                    to={ent.ROLE.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        'app.menu.setting.submenu.role.name',
                        { smart_count: 2 }
                    )}
                    leftIcon={<role.icon />}
                    dense={dense}
                />
                <MenuItemLink
                    to={ent.USER.NAME}
                    state={{ _scrollToTop: true }}
                    primaryText={translate(
                        'app.menu.setting.submenu.user.name',
                        { smart_count: 2 }
                    )}
                    leftIcon={<user.icon />}
                    dense={dense}
                />
            </Submenu>
        </Box>
    );
};
